import React from 'react';
import { Box, TextField, Chip, IconButton, InputAdornment, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon, ViewList as ViewListIcon, ViewModule as ViewModuleIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../theme';
import { useTranslation } from 'react-i18next';

const AlertsSearchFilters = ({
  searchQuery,
  setSearchQuery,
  selectedPriority,
  setSelectedPriority,
  selectedType,
  setSelectedType,
  days,
  setDays,
  alertTypes,
  getAlertTypeLabel,
  groupByTicker,
  setGroupByTicker,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
      }}
    >
      {/* Left Side: Search + Priority */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
        {/* Search */}
        <TextField
          placeholder={t('alerts.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  edge="end"
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: { xs: '100%', sm: 280 },
            flex: { xs: '1 1 100%', sm: '1 1 auto' },
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              fontSize: '0.8125rem',
              height: 32,
              '& fieldset': {
                borderColor: 'divider',
              },
            },
          }}
        />

        {/* Priority Chips */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[
            { key: 'ALL', label: t('alerts.filters.allPriority'), color: colors.purple.main },
            { key: 'HIGH', label: t('alerts.filters.high'), color: colors.success.main },
            { key: 'MEDIUM', label: t('alerts.filters.medium'), color: colors.warning.main },
            { key: 'LOW', label: t('alerts.filters.low'), color: colors.info.main },
          ].map(p => {
            const isSelected = selectedPriority === p.key;
            const chipColor = p.color;
            return (
              <Chip
                key={p.key}
                label={p.label}
                size="small"
                onClick={() => setSelectedPriority(p.key)}
                sx={{
                  bgcolor: isSelected ? chipColor : 'transparent',
                  color: isSelected ? '#fff' : 'text.primary',
                  border: `1px solid ${isSelected ? chipColor : 'divider'}`,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 26,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: isSelected ? chipColor : alpha(chipColor, 0.1),
                    borderColor: chipColor,
                  },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Right Side: Type + Range + Group Toggle */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ml: { xs: 0, sm: 'auto' } }}>
        {/* Group By Toggle */}
        <Tooltip title={groupByTicker ? t('alerts.filters.showAll') : t('alerts.filters.groupByTicker')}>
          <ToggleButtonGroup
            value={groupByTicker ? 'grouped' : 'all'}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setGroupByTicker(newValue === 'grouped');
              }
            }}
            size="small"
            sx={{
              height: 32,
              '& .MuiToggleButton-root': {
                px: 1,
                py: 0.5,
                fontSize: '0.75rem',
                border: `1px solid ${colors.gray[300]}`,
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: colors.purple.main,
                  color: '#fff',
                  borderColor: colors.purple.main,
                  '&:hover': {
                    bgcolor: colors.purple.dark,
                  },
                },
              },
            }}
          >
            <ToggleButton value="all">
              <ViewListIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t('alerts.filters.all')}
            </ToggleButton>
            <ToggleButton value="grouped">
              <ViewModuleIcon sx={{ fontSize: 16, mr: 0.5 }} />
              {t('alerts.filters.grouped')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        {/* Type Select */}
        <TextField
          size="small"
          select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          slotProps={{
            select: { native: true }
          }}
          sx={{
            minWidth: 110,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              fontSize: '0.8125rem',
              height: 32,
              '& fieldset': {
                borderColor: 'divider',
              },
            },
          }}
        >
          <option value="ALL">{t('alerts.filters.allTypes')}</option>
          {alertTypes.map(type => (
            <option key={type} value={type}>{getAlertTypeLabel(type)}</option>
          ))}
        </TextField>

        {/* Range Select */}
        <TextField
          size="small"
          select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          slotProps={{
            select: { native: true }
          }}
          sx={{
            minWidth: 70,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              fontSize: '0.8125rem',
              height: 32,
              '& fieldset': {
                borderColor: 'divider',
              },
            },
          }}
        >
          <option value={1}>1d</option>
          <option value={7}>7d</option>
          <option value={14}>14d</option>
          <option value={30}>30d</option>
          <option value={60}>60d</option>
        </TextField>
      </Box>
    </Box>
  );
};

export default AlertsSearchFilters;

